import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, Loader2, User } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

interface CommentFormProps {
  onSubmitComment: (content: string) => Promise<void>;
  submitting: boolean;
}

export default function CommentForm({ onSubmitComment, submitting }: CommentFormProps) {
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!content.trim()) {
      setError('Please enter a comment');
      return;
    }

    try {
      await onSubmitComment(content);
      setContent('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to post comment');
    }
  };

  if (!user) {
    return (
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 text-center">
        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
            <User className="h-6 w-6 text-primary-600" />
          </div>
        </div>
        <h3 className="font-semibold mb-2">Join the Conversation</h3>
        <p className="text-gray-600 mb-4">
          Sign in to share your thoughts and connect with fellow gardeners.
        </p>
        <Link
          to="/auth"
          className="inline-flex items-center gap-2 bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
        >
          <User className="h-4 w-4" />
          <span>Sign In to Comment</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border p-6">
      <h3 className="text-lg font-semibold mb-4">Leave a Comment</h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Share your thoughts, experiences, or questions about this article..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 resize-vertical"
            disabled={submitting}
          />
        </div>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting || !content.trim()}
            className="flex items-center gap-2 bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            <span>Post Comment</span>
          </button>
        </div>
      </form>
    </div>
  );
}